import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../api';
import {
  fuelingHistoryService,
  type FuelingHistory,
} from '../../api/fueling.history';
import {
  Fuel,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Loader2,
  AlertCircle,
  Eye,
  Car,
  User,
  Building2,
  Droplets,
  Calendar,
  Clock,
  FileText,
  X,
  ImageIcon,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
} from '../../components/ui/SharedLayout';

const ITEMS_PER_PAGE = 20;

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR');
};

const formatDateOnly = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const formatTimeOnly = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

type HistoryView = 'todos' | 'auditoria' | 'finalizados';

const getHistoryEntryType = (status?: string) => {
  const normalizedStatus = (status || '').toLowerCase();
  return normalizedStatus === 'aprovado' || normalizedStatus === 'rejeitado'
    ? 'Auditoria'
    : 'Finalização';
};

const HistoricoDeAbastecimento = () => {
  const { setPageTitle } = useAuth();
  const [history, setHistory] = useState<FuelingHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<HistoryView>('todos');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedHistory, setSelectedHistory] = useState<FuelingHistory | null>(
    null,
  );
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [expandedBoleto, setExpandedBoleto] = useState<string | null>(null);

  useEffect(() => {
    setPageTitle('Histórico de abastecimentos do setor');
  }, [setPageTitle]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fuelingHistoryService.getAllFuelingHistory();
      setHistory(data);
    } catch (loadError) {
      setHistory([]);
      setError(
        getApiErrorMessage(
          loadError,
          'Erro ao carregar o histórico de abastecimento.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredHistory = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const viewFiltered = history.filter((item) => {
      const status = (item.status || '').toLowerCase();

      if (view === 'finalizados') return status === 'finalizado';
      if (view === 'auditoria') return status === 'aprovado' || status === 'rejeitado';
      return true;
    });

    return viewFiltered.filter((item) => {
      if (!term) return true;

      return [
        item.vehicle?.brand || '',
        item.vehicle?.license_plate || '',
        item.vehicle?.description || '',
        item.employee?.name || '',
        item.employee?.cpf || '',
        item.provider?.name || '',
        item.fuel_type || '',
        item.status || '',
        item.action || '',
        item.details || '',
        item.invoice_number || '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [history, searchTerm, view]);

  const summary = useMemo(() => {
    const finalizados = history.filter(
      (item) => (item.status || '').toLowerCase() === 'finalizado',
    ).length;
    const auditoria = history.filter((item) => {
      const status = (item.status || '').toLowerCase();
      return status === 'aprovado' || status === 'rejeitado';
    }).length;

    return {
      total: history.length,
      finalizados,
      auditoria,
    };
  }, [history]);

  const totalPages = Math.max(1, Math.ceil(filteredHistory.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredHistory.length, currentPage, totalPages]);

  const { historyPaginado, pageRange } = useMemo(() => {
    const total = filteredHistory.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return {
      historyPaginado: filteredHistory.slice(startIndex, endIndex),
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredHistory, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const renderTipoBadge = (status?: string) => {
    const tipo = getHistoryEntryType(status);
    if (tipo === 'Auditoria') {
      return (
        <Badge tone="blue" className="gap-1">
          Auditoria
        </Badge>
      );
    }
    return (
      <Badge tone="slate" className="gap-1">
        Finalização
      </Badge>
    );
  };

  const renderStatusBadge = (status?: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'aprovado':
        return (
          <Badge tone="emerald" className="gap-1.5">
            Aprovado
          </Badge>
        );
      case 'rejeitado':
        return (
          <Badge tone="red" className="gap-1.5">
            Rejeitado
          </Badge>
        );
      case 'finalizado':
        return (
          <Badge tone="slate" className="gap-1.5">
            Finalizado
          </Badge>
        );
      default:
        return (
          <Badge tone="amber" className="gap-1.5">
            {status || '-'}
          </Badge>
        );
    }
  };

  const boletoUrl = selectedHistory?.boleto_photo?.url;

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedHistory(null);
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Histórico de abastecimentos do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Histórico — Abastecimentos
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Finalizações e trilha de auditoria
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                do seu departamento
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button
              variant="secondary"
              onClick={() => void loadData()}
              className="h-10 px-4"
            >
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
            <select
              value={view}
              onChange={(event) => {
                setView(event.target.value as HistoryView);
                setCurrentPage(1);
              }}
              className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-[13px] font-semibold text-slate-700 shadow-sm transition-all hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:ring-offset-1 cursor-pointer appearance-none pr-9 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 20 20%22 fill=%22%2364748b%22><path fill-rule=%22evenodd%22 d=%22M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z%22 clip-rule=%22evenodd%22 /></svg>')] bg-no-repeat bg-[right_14px_center] bg-[16px]"
            >
              <option value="todos">📋 Todos os registros</option>
              <option value="finalizados">🧾 Apenas finalizações</option>
              <option value="auditoria">✅ Apenas auditoria (aprov./rejeit.)</option>
            </select>
          </div>
        </div>

        <div className="mb-5 flex flex-wrap items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 shadow-sm text-[13px] font-semibold text-slate-700">
            <span className="h-2 w-2 rounded-full bg-slate-500" />
            Total: {summary.total}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 shadow-sm text-[13px] font-semibold text-blue-700">
            <span className="h-2 w-2 rounded-full bg-blue-500" />
            Auditoria: {summary.auditoria}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 shadow-sm text-[13px] font-semibold text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            Finalizações: {summary.finalizados}
          </span>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 inline-flex items-start gap-3 w-full">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <span>{error}</span>
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 min-w-[44px] min-h-[44px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-[0_8px_22px_-10px_rgba(16,185,129,0.7)]">
                  <Fuel className="h-[22px] w-[22px] stroke-[1.9]" />
                </div>
                <div>
                  <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                    Registros de abastecimento
                  </h2>
                  <p className="mt-0.5 text-[12.5px] font-medium leading-tight text-slate-500">
                    Finalizações, aprovações e rejeições (auditoria)
                  </p>
                </div>
              </div>
              <div className="relative w-full sm:w-[360px] shrink-0">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  placeholder="Buscar veículo, motorista, fornecedor, NF, combustível..."
                  className="!pl-10 h-10"
                />
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {loading && history.length === 0 ? (
              <div className="flex items-center justify-center gap-2.5 py-16">
                <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                <span className="text-[13.5px] font-semibold text-slate-600">
                  Carregando histórico...
                </span>
              </div>
            ) : (
              <ModernTable>
                <Thead>
                  <Tr>
                    <Th className="w-[12%] min-w-[180px]">Veículo</Th>
                    <Th className="w-[12%] min-w-[180px]">Motorista</Th>
                    <Th className="w-[10%] min-w-[170px]">Fornecedor</Th>
                    <Th className="w-[7%] min-w-[120px]">Combustível</Th>
                    <Th className="w-[6%] min-w-[80px]">Litros</Th>
                    <Th className="w-[8%] min-w-[110px]">Valor</Th>
                    <Th className="w-[10%] min-w-[150px]">Data</Th>
                    <Th className="w-[7%] min-w-[110px]">Tipo</Th>
                    <Th className="w-[8%] min-w-[110px]">Status</Th>
                    <Th className="w-[9%] min-w-[130px]">Ação</Th>
                    <Th className="w-[4%] min-w-[80px]">Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {historyPaginado.length === 0 ? (
                    <Tr>
                      <Td colSpan={11}>
                        <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                            <Fuel className="h-7 w-7 stroke-[1.8]" />
                          </div>
                          <div className="text-[14px] font-bold text-slate-700">
                            Nenhum registro no histórico
                          </div>
                          <p className="max-w-sm text-[12.5px] font-medium leading-relaxed text-slate-500">
                            {searchTerm || view !== 'todos'
                              ? 'Nenhum resultado para os filtros aplicados. Tente outros termos.'
                              : 'Os registros de abastecimento aparecerão aqui automaticamente.'}
                          </p>
                        </div>
                      </Td>
                    </Tr>
                  ) : (
                    historyPaginado.map((item) => (
                      <Tr key={item.id}>
                        <Td>
                          <div
                            className="min-w-0 w-full max-w-[240px] overflow-hidden"
                            title={[item.vehicle?.brand, item.vehicle?.description, item.vehicle?.license_plate].filter(Boolean).join(' • ')}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200">
                                <Car className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block max-w-[170px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                  {item.vehicle?.brand || item.vehicle?.description || `Veículo #${item.vehicle_id}`}
                                </span>
                                <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 inline-flex items-center gap-1">
                                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-slate-600 font-[11px] font-semibold tabular-nums tracking-wide">
                                    {item.vehicle?.license_plate || 'Sem placa'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div
                            className="min-w-0 w-full max-w-[240px] overflow-hidden"
                            title={[item.employee?.name, item.employee?.cpf].filter(Boolean).join(' • CPF: ')}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-teal-50 text-teal-700 ring-1 ring-inset ring-teal-200">
                                <User className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block max-w-[170px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                  {item.employee?.name || `Motorista #${item.employee_id}`}
                                </span>
                                <div className="mt-0.5 text-[11.5px] font-medium leading-tight text-slate-500 tabular-nums">
                                  {item.employee?.cpf || '-'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div
                            className="min-w-0 w-full max-w-[200px] overflow-hidden"
                            title={item.provider?.name || String(item.provider_id)}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className="mt-0.5 shrink-0 flex h-9 w-9 min-w-[36px] min-h-[36px] items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200">
                                <Building2 className="h-[18px] w-[18px] stroke-[1.9]" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <span className="block max-w-[140px] truncate text-[13.5px] font-semibold leading-tight text-slate-900">
                                  {item.provider?.name || `Fornecedor #${item.provider_id}`}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div
                            className="min-w-0 w-full overflow-hidden"
                            title={item.fuel_type || '-'}
                          >
                            <div className="inline-flex items-center gap-1.5 rounded-lg bg-teal-50 px-2 py-1 ring-1 ring-inset ring-teal-200">
                              <Droplets className="h-3.5 w-3.5 text-teal-700 stroke-[1.9]" />
                              <span className="max-w-[90px] truncate text-[12.5px] font-semibold text-teal-800">
                                {item.fuel_type || '-'}
                              </span>
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <span className="inline-flex items-center h-8 min-w-[70px] justify-center px-2 rounded-lg bg-slate-50 text-[13px] font-bold tabular-nums text-slate-700 ring-1 ring-inset ring-slate-200">
                            {item.amount}L
                          </span>
                        </Td>
                        <Td>
                          <span className="inline-flex items-center h-8 min-w-[100px] justify-center px-2.5 rounded-lg bg-emerald-50 text-[13px] font-bold tabular-nums text-emerald-800 ring-1 ring-inset ring-emerald-200">
                            {formatCurrency(item.total_cost)}
                          </span>
                        </Td>
                        <Td>
                          <div>
                            <div className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-700 tabular-nums">
                              <Calendar className="h-[15px] w-[15px] text-slate-400" />
                              {formatDateOnly(item.fueling_time || item.created_at)}
                            </div>
                            <div className="mt-1 inline-flex items-center gap-1.5 text-[11.5px] font-medium text-slate-500 tabular-nums">
                              <Clock className="h-[13px] w-[13px] text-slate-400" />
                              {formatTimeOnly(item.fueling_time || item.created_at)}
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div className="flex items-center justify-start">
                            {renderTipoBadge(item.status)}
                          </div>
                        </Td>
                        <Td>
                          <div className="flex items-center justify-start">
                            {renderStatusBadge(item.status)}
                          </div>
                        </Td>
                        <Td>
                          <div
                            className="min-w-0 w-full max-w-[170px] overflow-hidden"
                            title={item.action || '-'}
                          >
                            <span className="block max-w-full truncate text-[13px] font-semibold leading-tight text-slate-800">
                              {item.action || '-'}
                            </span>
                          </div>
                        </Td>
                        <Td>
                          <div className="flex items-center justify-center gap-1.5">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setSelectedHistory(item);
                                setIsViewModalOpen(true);
                              }}
                              title="Visualizar detalhes"
                              className="h-8 w-8 rounded-lg text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 hover:ring-emerald-200"
                            >
                              <Eye className="h-4 w-4 stroke-[1.9]" />
                            </Button>
                          </div>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </ModernTable>
            )}

            {pageRange.total > 0 ? (
              <div className="mt-4 flex flex-col gap-3 border-t border-slate-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-[12.5px] font-medium leading-tight text-slate-500">
                  Mostrando{' '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.start}
                  </span>
                  {'–'}{' '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.end}
                  </span>
                  {' de '}
                  <span className="font-bold tabular-nums text-slate-800">
                    {pageRange.total}
                  </span>
                  {' registro(s)'}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:ring-offset-1"
                    aria-label="Primeira página"
                  >
                    <ChevronsLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:ring-offset-1"
                    aria-label="Página anterior"
                  >
                    <ChevronLeft className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <div className="inline-flex items-center gap-1 mx-1">
                    {getPageNumbers.map((n, idx) =>
                      n === '...' ? (
                        <span
                          key={`dots-${idx}`}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                        >
                          …
                        </span>
                      ) : (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setCurrentPage(n)}
                          className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:ring-offset-1 ${
                            n === currentPage
                              ? 'bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-[0_4px_14px_-4px_rgba(16,185,129,0.55)]'
                              : 'border border-slate-200 bg-white text-slate-700 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700'
                          }`}
                        >
                          {n}
                        </button>
                      ),
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:ring-offset-1"
                    aria-label="Próxima página"
                  >
                    <ChevronRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                    className="inline-flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition-all duration-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-emerald-400/40 focus:ring-offset-1"
                    aria-label="Última página"
                  >
                    <ChevronsRight className="h-4 w-4 stroke-[1.8]" />
                  </button>
                </div>
              </div>
            ) : null}
          </CardBody>
        </SectionCard>
      </div>

      {isViewModalOpen && selectedHistory && (
        <Modal
          onClose={closeViewModal}
          maxWidthClass="max-w-4xl"
          title={
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 min-w-[40px] min-h-[40px] shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.65)]">
                <Eye className="h-5 w-5 stroke-[1.9]" />
              </div>
              <div>
                <div className="text-[16px] font-bold tracking-tight text-slate-900 leading-none">
                  Detalhes do Histórico — Registro #{selectedHistory.id}
                </div>
                <div className="mt-1 text-[12px] font-medium text-slate-500 leading-tight">
                  Veja todos os campos e a foto do boleto/comprovante
                </div>
              </div>
            </div>
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:items-center">
              <button
                type="button"
                onClick={closeViewModal}
                className="inline-flex h-11 items-center justify-center gap-1.5 rounded-xl border border-slate-200 bg-white px-5 text-[13px] font-semibold text-slate-700 transition-all hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/30"
              >
                Fechar
              </button>
            </div>
          }
        >
          <div className="space-y-5">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-blue-500 to-indigo-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-blue-700">
                  Dados do Abastecimento
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50/40 p-4 sm:p-5 space-y-4">
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Tipo do registro</span>
                    <div className="flex items-center">{renderTipoBadge(selectedHistory.status)}</div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Status</span>
                    <div className="flex items-center">{renderStatusBadge(selectedHistory.status)}</div>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Ação</span>
                    <span className="text-sm text-slate-900 font-medium">
                      {selectedHistory.action || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Veículo</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {selectedHistory.vehicle?.brand || selectedHistory.vehicle?.description || `Veículo #${selectedHistory.vehicle_id}`}
                    </p>
                    <p className="text-xs text-slate-500 tabular-nums">
                      {selectedHistory.vehicle?.license_plate || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Motorista</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {selectedHistory.employee?.name || `Motorista #${selectedHistory.employee_id}`}
                    </p>
                    <p className="text-xs text-slate-500 tabular-nums">
                      {selectedHistory.employee?.cpf || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Fornecedor</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {selectedHistory.provider?.name || `Fornecedor #${selectedHistory.provider_id}`}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Combustível</span>
                    <p className="text-sm text-slate-900 font-medium">
                      {selectedHistory.fuel_type}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Quantidade</span>
                    <p className="text-sm text-slate-900 font-bold tabular-nums">
                      {selectedHistory.amount} L
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Quilometragem</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">
                      {selectedHistory.odometer_reading ? `${selectedHistory.odometer_reading} km` : '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Nota fiscal</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">
                      {selectedHistory.invoice_number || '-'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-semibold text-slate-500">Data do abastecimento</span>
                    <p className="text-sm text-slate-900 font-medium tabular-nums">
                      {formatDate(selectedHistory.fueling_time || selectedHistory.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1.5 md:col-span-3">
                    <span className="text-xs font-semibold text-slate-500">
                      💰 Valor total do abastecimento
                    </span>
                    <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 px-4 py-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                      <span className="text-[22px] font-black tabular-nums tracking-tight text-emerald-800 leading-none">
                        {formatCurrency(selectedHistory.total_cost)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="flex flex-col gap-2">
                <div className="mb-0 flex items-center gap-2">
                  <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-indigo-500 to-purple-600" />
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-indigo-700">
                    Observações
                  </h3>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 h-full">
                  <span className="text-xs font-semibold text-slate-500 mb-2 block">
                    <span className="inline-flex items-center gap-1">
                      <FileText className="h-3.5 w-3.5 stroke-[1.9] text-slate-400" />
                      Texto original
                    </span>
                  </span>
                  <p className="text-[13px] font-medium leading-relaxed text-slate-800 whitespace-pre-wrap min-h-[80px]">
                    {selectedHistory.notes || '-'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="mb-0 flex items-center gap-2">
                  <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-orange-500 to-red-600" />
                  <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-orange-700">
                    Detalhes (histórico)
                  </h3>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-white p-4 h-full">
                  <p className="text-[13px] font-medium leading-relaxed text-slate-800 whitespace-pre-wrap min-h-[80px]">
                    {selectedHistory.details || '-'}
                  </p>
                </div>
              </div>
            </div>

            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="h-6 w-1 shrink-0 rounded-full bg-gradient-to-b from-amber-500 to-orange-600" />
                <h3 className="text-[13px] font-bold uppercase tracking-[0.14em] text-amber-700">
                  Boleto / Comprovante
                </h3>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
                {boletoUrl ? (
                  <button
                    type="button"
                    onClick={() => setExpandedBoleto(boletoUrl)}
                    className="group relative block w-full max-w-3xl mx-auto overflow-hidden rounded-xl border border-slate-200 shadow-sm transition-all hover:shadow-lg"
                    title="Clique para ampliar"
                  >
                    <div className="aspect-[4/3] w-full bg-slate-50 flex items-center justify-center">
                      <img
                        src={boletoUrl}
                        alt="Boleto do abastecimento"
                        className="h-full w-full object-contain transition-all group-hover:scale-[1.01]"
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent p-3.5 opacity-0 transition-opacity group-hover:opacity-100">
                      <span className="text-[12px] font-bold text-white drop-shadow">
                        🔍 Clique para ampliar o comprovante
                      </span>
                    </div>
                  </button>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 py-12 text-slate-400">
                    <ImageIcon className="h-10 w-10 stroke-[1.7]" />
                    <span className="text-[13px] font-semibold">
                      Nenhum boleto ou comprovante anexado a este registro
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {expandedBoleto && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 p-4 animate-[fadeIn_0.15s_ease-out]"
          onClick={() => setExpandedBoleto(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white ring-1 ring-inset ring-white/20 backdrop-blur-sm transition-all hover:bg-white/20 hover:scale-105"
            onClick={() => setExpandedBoleto(null)}
            aria-label="Fechar"
          >
            <X className="h-6 w-6 stroke-[2]" />
          </button>
          <img
            src={expandedBoleto}
            alt="Comprovante ampliado"
            className="max-h-[90vh] max-w-[90vw] rounded-2xl border-4 border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] bg-white"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default HistoricoDeAbastecimento;
